import React from 'react';
import { Button, Spinner, Badge } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import moment from 'moment';

export const AISummaryReport = ({ report, loadingState, triggerFn, logs, consoleEndRef }) => {
  const renderConsoleLogs = (consoleLogs) => {
    return (
      <div 
        className="bg-dark text-success p-3 rounded mb-3" 
        style={{
          fontFamily: "'Courier New', Courier, monospace",
          fontSize: '12px',
          height: '240px',
          overflowY: 'auto',
          border: '1px solid #1a1a1a',
          boxShadow: 'inset 0 0 10px rgba(0, 0, 0, 0.8)',
          backgroundColor: '#0a0a0a'
        }}
      >
        {consoleLogs.map((log, index) => {
          let color = '#39ff14'; // neon green
          if (log.startsWith('[SYSTEM]')) color = '#00ffff'; // neon cyan
          if (log.startsWith('[ERROR]')) color = '#ff073a'; // neon red
          if (log.startsWith('[SYSTEM ERROR]')) color = '#ff007f'; // deep pink
          
          return (
            <div key={index} style={{ color, marginBottom: '2px', lineHeight: '1.4' }}>
              <span style={{ opacity: 0.4, marginRight: '6px' }}>&gt;</span>
              {log}
            </div>
          );
        })}
        <div className="d-flex align-items-center mt-1" style={{ color: '#39ff14' }}>
          <span className="mr-2">&gt;</span>
          <span className="spinner-grow spinner-grow-sm text-success mr-2" role="status" style={{ width: '6px', height: '6px' }}></span>
          <span className="font-italic text-muted small">Running Bookeeping AI review tasks...</span>
        </div>
        <div ref={consoleEndRef} />
      </div>
    );
  };

  if (!report) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-robot fa-3x text-muted mb-3"></i>
        <h5>No AI analysis generated for this period</h5>
        <p className="text-muted small">Trigger the AI to analyze your data for correctness and insights.</p>
        <Button 
          variant="primary" 
          size="sm" 
          onClick={triggerFn}
          disabled={loadingState}
        >
          {loadingState ? <><Spinner animation="border" size="sm" className="mr-2" /> Requesting...</> : 'Trigger AI Review'}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
        <div>
          <h5 className="mb-0">{report.type === 'final_audit' ? 'Integrity Audit Results' : 'Narrative Analysis'}</h5>
          <div className="d-flex align-items-center">
            <small className="text-muted mr-2">Generated by AI</small>
            {report.dateGenerated && (
              <small className="text-muted border-left pl-2">
                <i className="far fa-clock mr-1"></i>
                {moment(report.dateGenerated).format('MMM D, YYYY h:mm A')}
              </small>
            )}
          </div>
        </div>
        <Badge variant={
          report.status === 'completed' ? 'success' : 
          report.status === 'failed' ? 'danger' : 'warning'
        }>
          {report.status.toUpperCase()}
        </Badge>
      </div>

      {report.status === 'pending' || report.status === 'processing' ? (
        <div>
          <div className="text-center py-3">
            <Spinner animation="grow" variant="primary" className="mb-2" />
            <h6>AI is performing a deep scan...</h6>
            <p className="text-muted extra-small mb-3">Analyzing financial records line-by-line. Live console stream below.</p>
          </div>
          {renderConsoleLogs(logs)}
        </div>
      ) : report.status === 'completed' ? (
        <div className="ai-report-content py-2">
          <ReactMarkdown components={{
            h3: ({node, ...props}) => <h5 className="text-primary mt-4 mb-2 border-bottom pb-1" {...props} />,
            h4: ({node, ...props}) => <h6 className="font-weight-bold mt-3" {...props} />,
            ul: ({node, ordered, ...props}) => <ul className="pl-3" {...props} />,
            li: ({node, ordered, ...props}) => <li className="mb-2" {...props} />,
            strong: ({node, ...props}) => <strong className="text-dark" {...props} />
          }}>
            {report.reportContent}
          </ReactMarkdown>
        </div>
      ) : (
        <div className="alert alert-danger">
          Failed to generate report. Please try again.
        </div>
      )}

      {report.status === 'completed' && (
        <div className="text-right mt-4 pt-3 border-top d-flex justify-content-end">
          <Button variant="outline-primary" size="sm" onClick={triggerFn}>
            Regenerate Review
          </Button>
        </div>
      )}
    </div>
  );
};
